/* eslint-disable camelcase */
/**
 * Single source for attaching TP/SL to an order the user is ABOUT to place.
 *
 * The mirror of `useTpslForPosition`, which reconciles triggers on a position that
 * already exists. Both funnel into `createTPSLOrders` (contexts/OrderContext/
 * useTPSLActions) and from there into the executor client in this directory.
 *
 * This owns the order form's TP/SL state as well as the submit-time call, so the
 * form files stay presentational and there is exactly one place where the
 * "attach triggers to a new order" rules live. Reading triggers back for display
 * is `useTpslDisplay` in this same directory.
 */
import { useCallback, useMemo, useState } from "react";

import { MarginTypeResponse, SideResponse } from "../../codegen-api";
import { RoeIssue, TpslLeg, roeFromTrigger, triggerFromRoe } from "../../utils/amount/roe";
import { useOrder } from "../api/order/useOrder";
import { useToast } from "../toast";
import { useSendOrderTpsl } from "./useOrderFormTpsl.attach";

export interface AttachTpslArgs {
  instrumentName: string;
  instrumentId: string;
  side: SideResponse | string;
  /** Order size, in the same units the order itself was submitted with. */
  amount: string | number;
  /**
   * The order's margin mode, ALREADY normalized. Pass the same `orderMarginType`
   * the order was submitted with rather than re-deriving it — for a reduce-only
   * market order that value comes from the existing position, not the form field,
   * and the executor must be told the mode the position actually uses.
   */
  marginType: MarginTypeResponse;
  /** What ROE% is measured from. Required for the triggers to resolve in ROE mode. */
  roeRef?: TpslRoeRef;
}

/** How the two legs are entered: trigger prices, or ROE% on the margin posted. */
export type TpslEntryMode = "price" | "roe";

/**
 * What an ROE% is measured from: the order's entry (the limit price, or the
 * mark for a market order), its leverage and the market's price grid.
 */
export interface TpslRoeRef {
  side: SideResponse | string;
  entry?: string | number | null;
  leverage?: number | string | null;
  priceStep: string | number;
}

/** The trigger prices the legs stand for right now, or why one cannot be had. */
export interface ResolvedTpsl {
  tp?: string;
  sl?: string;
  /** A sentence for the first leg that was entered but cannot be priced. */
  issue?: string;
}

export interface OrderFormTpsl {
  /** True only where the chain has a trigger executor AND a verifying contract. */
  supported: boolean;
  enabled: boolean;
  tpPrice: string;
  slPrice: string;
  setTpPrice: (value: string) => void;
  setSlPrice: (value: string) => void;
  /** Price or ROE%. Switching carries typed values across when an entry is known. */
  mode: TpslEntryMode;
  setMode: (mode: TpslEntryMode, ref?: TpslRoeRef) => void;
  tpRoe: string;
  slRoe: string;
  setTpRoe: (value: string) => void;
  setSlRoe: (value: string) => void;
  /** The trigger prices for `ref` — what a preview shows and what submit sends. */
  resolve: (ref?: TpslRoeRef) => ResolvedTpsl;
  /** Flips the checkbox, clearing both prices when switching off. */
  toggle: () => void;
  /**
   * Force the control back to off and empty. Callers use this when TP/SL stops
   * being applicable at all — e.g. the order becomes reduce-only — so that entered
   * prices can never linger in state and be silently dropped at submit time.
   */
  reset: () => void;
  attachToOrder: (args: AttachTpslArgs) => Promise<void>;
}

/** A leg counts only when it parses to a positive price. */
const wants = (raw: string): boolean => {
  const value = Number(raw);
  return Number.isFinite(value) && value > 0;
};

const sideOf = (side: SideResponse | string): "buy" | "sell" =>
  String(side).toLowerCase() === SideResponse.Sell ? "sell" : "buy";

const LEG_NAME: Record<TpslLeg, string> = { tp: "Take profit", sl: "Stop loss" };

const roeIssueText = (leg: TpslLeg, issue: RoeIssue, typed: string): string => {
  if (issue === "beyond_zero") {
    return `${LEG_NAME[leg]} at ${typed}% ROE is past a price of zero at this leverage — the position would be liquidated first. Use a smaller ROE%.`;
  }
  if (issue === "at_entry") {
    return `${LEG_NAME[leg]} at ${typed}% ROE is less than one price step from the entry. Use a larger ROE%.`;
  }
  return `${LEG_NAME[leg]}: enter a positive ROE%, and a limit price (or wait for the mark) so it can be turned into a trigger price.`;
};

export function useOrderFormTpsl(): OrderFormTpsl {
  const { createTPSLOrders, tpslSupported } = useOrder();
  const { addErrorToast } = useToast();
  const sendTpsl = useSendOrderTpsl(createTPSLOrders, addErrorToast);

  // Local state rather than react-hook-form fields: both legs are optional and a
  // blank value must never block submission, whereas the form's validators run
  // across every registered field. The executor re-validates server-side.
  const [enabled, setEnabled] = useState(false);
  const [tpPrice, setTpPrice] = useState("");
  const [slPrice, setSlPrice] = useState("");
  const [mode, setModeState] = useState<TpslEntryMode>("price");
  const [tpRoe, setTpRoe] = useState("");
  const [slRoe, setSlRoe] = useState("");

  const resolve = useCallback(
    (ref?: TpslRoeRef): ResolvedTpsl => {
      if (mode === "price") {
        return {
          tp: wants(tpPrice) ? tpPrice : undefined,
          sl: wants(slPrice) ? slPrice : undefined,
        };
      }
      const out: ResolvedTpsl = {};
      const legs: [TpslLeg, string][] = [
        ["tp", tpRoe],
        ["sl", slRoe],
      ];
      for (const [leg, typed] of legs) {
        if (typed.trim() === "") continue; // not wanted
        const trigger = ref
          ? triggerFromRoe({
              side: sideOf(ref.side),
              leg,
              entry: ref.entry,
              leverage: ref.leverage,
              roePct: typed,
              priceStep: ref.priceStep,
            })
          : { issue: "invalid" as const };
        if (trigger.price) out[leg] = trigger.price;
        else if (!out.issue) out.issue = roeIssueText(leg, trigger.issue ?? "invalid", typed);
      }
      return out;
    },
    [mode, slPrice, slRoe, tpPrice, tpRoe]
  );

  const setMode = useCallback(
    (next: TpslEntryMode, ref?: TpslRoeRef) => {
      if (next === mode) return;
      // Carry what was typed across, so switching is a change of view rather
      // than a way to lose a value. Without an entry there is nothing to
      // convert against, and the other field simply starts empty.
      if (next === "roe") {
        const toRoe = (leg: TpslLeg, price: string) =>
          ref
            ? roeFromTrigger({
                side: sideOf(ref.side),
                leg,
                entry: ref.entry,
                leverage: ref.leverage,
                price,
              }) ?? ""
            : "";
        setTpRoe(toRoe("tp", tpPrice));
        setSlRoe(toRoe("sl", slPrice));
      } else {
        const priced = resolve(ref);
        setTpPrice(priced.tp ?? "");
        setSlPrice(priced.sl ?? "");
      }
      setModeState(next);
    },
    [mode, resolve, slPrice, tpPrice]
  );

  const toggle = useCallback(() => {
    // Clearing happens here rather than inside a setEnabled updater: updaters must
    // be pure, and React invokes them twice under StrictMode.
    if (enabled) {
      // Never leave stale prices behind a hidden checkbox.
      setTpPrice("");
      setSlPrice("");
      setTpRoe("");
      setSlRoe("");
    }
    setEnabled(!enabled);
  }, [enabled]);

  const reset = useCallback(() => {
    setEnabled(false);
    setTpPrice("");
    setSlPrice("");
    setTpRoe("");
    setSlRoe("");
  }, []);

  const attachToOrder = useCallback(
    async ({
      instrumentName,
      instrumentId,
      side,
      amount,
      marginType,
      roeRef,
    }: AttachTpslArgs) => {
      if (!enabled || !tpslSupported) return;

      // In ROE mode the triggers are priced from the order that was actually
      // placed; a leg that cannot be priced is reported, never dropped quietly.
      const resolved = resolve(roeRef);
      const tpTrigger = resolved.tp ?? "";
      const slTrigger = resolved.sl ?? "";
      const wantTp = wants(tpTrigger);
      const wantSl = wants(slTrigger);
      if (resolved.issue) {
        addErrorToast(<p>Order placed — TP/SL NOT set</p>, resolved.issue);
        return;
      }
      if (!wantTp && !wantSl) return;

      // The executor derives what it needs from instrument + side + margin mode,
      // so the just-placed order is described directly rather than waiting for
      // the backend to index the new position — which a limit order may never
      // do until it fills. A failure is reported, never thrown: the order is
      // already on-chain, and the user ticked TP/SL so it must not be silent.
      const ok = await sendTpsl({
        instrumentName,
        instrumentId,
        side,
        amount,
        marginType,
        tpTrigger: wantTp ? tpTrigger : undefined,
        slTrigger: wantSl ? slTrigger : undefined,
      });
      if (ok) {
        setTpPrice("");
        setSlPrice("");
        setTpRoe("");
        setSlRoe("");
        setEnabled(false);
      }
    },
    [addErrorToast, enabled, resolve, sendTpsl, tpslSupported]
  );

  return useMemo(
    () => ({
      supported: tpslSupported,
      enabled,
      tpPrice,
      slPrice,
      setTpPrice,
      setSlPrice,
      mode,
      setMode,
      tpRoe,
      slRoe,
      setTpRoe,
      setSlRoe,
      resolve,
      toggle,
      reset,
      attachToOrder,
    }),
    [
      attachToOrder,
      enabled,
      mode,
      reset,
      resolve,
      setMode,
      slPrice,
      slRoe,
      toggle,
      tpPrice,
      tpRoe,
      tpslSupported,
    ]
  );
}
